'use client';

import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Flex, Text } from '@mezzoforte/forge';
import { formatCurrency } from '@/util/currency';
import { priceWithVat } from '@/util/price';
import { CurrencyInput } from '@/components/CurrencyInput/CurrencyInput';
import useBid from '@/hooks/useBid';
import { BiddingContext } from '@/context/BiddingContext';
import InfoTooltip from '@/components/InfoTooltip/InfoTooltip';
import { useHermesEntry } from '@/hooks/useHermesEntry';

interface BiddingInputsProps {
  isMinBidLocked: boolean;
  showAutobid: boolean;
}

export function BiddingInputs({ isMinBidLocked, showAutobid }: BiddingInputsProps) {
  const { entryId, vatPerc, minimumRaise, processingCostInfo, deliveryMethods, startPrice } =
    useContext(BiddingContext);
  const { hermesEntry } = useHermesEntry(entryId, vatPerc);
  const minRaiseWithVat = priceWithVat(minimumRaise, vatPerc);
  const minBidWithVat = priceWithVat(hermesEntry?.highestBid ?? startPrice, vatPerc) + minRaiseWithVat;

  const [bidValue, setBidValue] = useState<number | null>(null);
  const bidInputRef = useRef<HTMLInputElement>(null);

  const [autobidValue, setAutobidValue] = useState<number | null>(null);
  const [autobidError, setAutobidError] = useState<null | string>(null);

  useEffect(() => {
    // Update input value when highest bid changes
    if (bidInputRef.current !== document.activeElement) {
      if ((bidValue === null || bidValue < minBidWithVat) && hermesEntry) {
        setBidValue(minBidWithVat);
      }
    }
  }, [hermesEntry, hermesEntry?.highestBid, bidValue, minBidWithVat]);

  const { confirmBid, BidModals } = useBid({
    safeLimit: hermesEntry?.safeLimit ?? null,
    minBidWithVat,
  });

  const validateAutobid = () => {
    if (autobidValue === null) return;
    if (autobidValue < minBidWithVat) {
      setAutobidError('Maksimitarjous on liian pieni');
      return;
    }
    void confirmBid(autobidValue, true);
  };

  const handleAutobidChange = (value: number | null) => {
    if (autobidError !== null && (value === null || value >= minBidWithVat)) {
      setAutobidError(null);
    }
    setAutobidValue(value);
  };

  const helperText = (() => {
    if (processingCostInfo && deliveryMethods?.length) return 'Hintaan lisätään käsittely- ja toimituskulut';
    if (deliveryMethods?.length) return 'Hintaan lisätään toimituskulut';
    if (processingCostInfo) return 'Hintaan lisätään käsittelykulu';

    return null;
  })();

  return (
    <Flex
      direction="column"
      gap={3}
    >
      <CurrencyInput
        ref={bidInputRef}
        value={bidValue}
        isReadOnly={isMinBidLocked}
        onValueChange={setBidValue}
        label={
          <>
            <Text
              variant="bold"
              display="inline-block"
              mr={2}
            >
              Tee tarjous
              <InfoTooltip
                label={`
                  Tarjouksena on oletuksena kohteen minimikorotuksen mukainen summa, mutta voit halutessasi tarjota kohteesta enemmänkin.
                  Tarjoa-napin painallus avaa näkymän, jossa näet tarjouksesi sisällön tarkemmin eriteltynä.
                  Tarjouksesi kirjautuu järjestelmään vasta, kun vahvistat sen erittelynäkymässä.`}
              />
            </Text>
            <Text
              display="inline-block"
              fontSize="sm"
              variant="subdued"
            >
              {isMinBidLocked
                ? `Voit korottaa vain ${formatCurrency(minRaiseWithVat)} verran`
                : `minimikorotus ${formatCurrency(minRaiseWithVat)}`}
            </Text>
          </>
        }
        helperText={helperText}
        mb={3}
        data-test="bid-input"
      >
        <Button
          variant="highlight"
          width="14ch"
          onClick={() => {
            if (!bidValue) {
              return;
            }
            void confirmBid(bidValue);
          }}
        >
          Tarjoa
        </Button>
      </CurrencyInput>
      {showAutobid && (
        <CurrencyInput
          value={autobidValue}
          isInvalid={!!autobidError}
          errorInfo={autobidError}
          isReadOnly={isMinBidLocked}
          onValueChange={handleAutobidChange}
          label={
            <Text
              variant="bold"
              display="inline-block"
            >
              Korotusautomaatti
              <InfoTooltip
                label={`
                Korotusautomaatilla voit määritellä maksimisumman, jonka olet valmis tarjoamaan ja automaatti tekee
                tarjoukset puolestasi. Syötä maksimitarjous ja paina "Aktivoi"-nappia, jolloin korotusautomaatti
                kytkeytyy päälle. Korotusautomaatti tarjoaa aina minimikorotuksen verran. Korotusautomaatti tekee
                heti ensimmäisen tarjouksen, mikäli sinulla ei ole korkeinta tarjousta. Kun toinen huutaja tekee
                korotuksen, tekee korotusautomaattisi välittömästi korotuksen tämän päälle. Korotusautomaatin voit
                kytkeä pois päältä koska tahansa. Mikäli haluat vaihtaa maksimisummaa, kytke korotusautomaatti pois
                päältä ja tee uusi automaatti haluamallasi summalla.`}
              />
            </Text>
          }
          helperText={helperText}
          mb={3}
          data-test="autobid-input"
        >
          <Button
            variant="highlight"
            width="14ch"
            onClick={validateAutobid}
          >
            Aktivoi
          </Button>
        </CurrencyInput>
      )}
      <BidModals />
    </Flex>
  );
}
